/**
 * Number formatting utilities for consistent display across all components.
 *
 * These follow the active UI language (see i18n/intlLocale.js): German renders
 * 1.234,56 and English renders 1,234.56. With German as the default display
 * language, output is unchanged from before i18n was introduced.
 *
 * NOTE the formatGerman* names below are kept as deprecated aliases only so
 * existing call sites keep working; prefer formatNumber/formatPercentage/
 * formatCurrency in new code. Callers must call useTranslation() somewhere in
 * the component if they want to re-render on a language switch — see the
 * consequence documented in i18n/index.js.
 */

import { getActiveIntlLocale } from '../i18n/intlLocale';

// Format numbers using the active locale's separators
export const formatNumber = (value, options = {}) => {
  if (value === undefined || value === null) return '0';

  // Handle strings that might contain non-numeric characters
  if (typeof value === 'string') {
    const parsed = parseFloat(value.toString().replace(',', '.'));
    if (isNaN(parsed)) return value;
    value = parsed;
  }

  // Convert to number
  const num = Number(value);
  if (isNaN(num)) return '0';

  return new Intl.NumberFormat(getActiveIntlLocale(), {
    maximumFractionDigits: options.decimals || 2,
    minimumFractionDigits: options.minDecimals || 0,
    ...options
  }).format(num);
};

// Format percentages (adds % symbol)
export const formatPercentage = (value, decimals = 2) => {
  if (value === undefined || value === null) return '0%';

  const percent = typeof value === 'number' ? value * 100 : parseFloat(value) * 100;
  if (isNaN(percent)) return '0%';

  return formatNumber(percent, { decimals, maximumFractionDigits: decimals }) + '%';
};

// Format currency if needed in future
export const formatCurrency = (value, currency = 'EUR') => {
  return new Intl.NumberFormat(getActiveIntlLocale(), {
    style: 'currency',
    currency: currency
  }).format(Number(value) || 0);
};

// Abbreviated form for very large numbers (e.g. "1,2 Mio." / "1.2M"), used as the last-resort
// fallback when a KPI tile is too narrow to show the full number even at its minimum readable
// font size. Locale-aware via Intl — never hardcode suffix strings.
export const formatCompactNumber = (value, decimals = 1) => {
  if (value === undefined || value === null) return '0';
  const num = Number(value);
  if (isNaN(num)) return String(value);

  return new Intl.NumberFormat(getActiveIntlLocale(), {
    notation: 'compact',
    maximumFractionDigits: decimals,
  }).format(num);
};

/** @deprecated Use formatNumber — output now follows the active UI language. */
export const formatGermanNumber = formatNumber;
/** @deprecated Use formatPercentage — output now follows the active UI language. */
export const formatGermanPercentage = formatPercentage;
/** @deprecated Use formatCurrency — output now follows the active UI language. */
export const formatGermanCurrency = formatCurrency;
